import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/services/supabaseClient";
import type { Tables, InsertTables, UpdateTables } from "@/types/database";

export type ClientWithOrganizer = Tables<"clients"> & {
  organizer?: Tables<"organizers"> | null;
};

async function fetchClients() {
  const { data, error } = await supabase
    .from("clients")
    .select("*, organizer:organizers(*)")
    .order("full_name");

  if (error) throw error;
  return data as ClientWithOrganizer[];
}

async function fetchClientById(id: string) {
  const { data, error } = await supabase
    .from("clients")
    .select("*, organizer:organizers(*)")
    .eq("id", id)
    .single();

  if (error) throw error;
  return data as ClientWithOrganizer;
}

async function createClient(client: InsertTables<"clients">) {
  const { data, error } = await supabase
    .from("clients")
    .insert([client])
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateClient(id: string, updates: UpdateTables<"clients">) {
  const { data, error } = await supabase
    .from("clients")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function deleteClient(id: string) {
  const { error } = await supabase.from("clients").delete().eq("id", id);

  if (error) throw error;
}

export function useClients() {
  return useQuery({
    queryKey: ["clients"],
    queryFn: fetchClients,
  });
}

export function useClient(id: string) {
  return useQuery({
    queryKey: ["client", id],
    queryFn: () => fetchClientById(id),
    enabled: !!id,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateTables<"clients">;
    }) => updateClient(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client", id] });
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}
